import React, { useMemo, useCallback } from "react";
import { IoAdd } from "react-icons/io5";
import WorkOrderList from "./List.js";
import WorkOrderForm from "./Form.js";
import { NavLink, useParams, useNavigate } from "react-router-dom";

// --- DYNAMIC PERMISSION LOGIC ---
const usePermissions = () => {
  const authData = useMemo(() => {
    try {
      const userDetailsString = localStorage.getItem("zTaskUserDetails");
      if (!userDetailsString) return { user: null, permissions: [] };
      const userDetails = JSON.parse(userDetailsString);
      const permissions = userDetails?.role?.permissions
        ? userDetails.role.permissions.map((p) => p.title)
        : [];
      return { user: userDetails, permissions };
    } catch (error) {
      console.error("Failed to parse user details from localStorage:", error);
      return { user: null, permissions: [] };
    }
  }, []);

  const hasPermission = useCallback(
    (requiredPermission) => {
      if (Array.isArray(requiredPermission)) {
        return requiredPermission.some((p) => authData.permissions.includes(p));
      }
      return authData.permissions.includes(requiredPermission);
    },
    [authData]
  );

  const canAccessModule = useCallback(() => {
    // A user can access the module if they have any permission related to work orders
    const workOrderPermissions = [
      "CREATE WORK ORDER",
      "VIEW WORK ORDER",
      "SET WORK ORDER",
      "DOWNLOAD WORK ORDER PDF",
      "VIEW ALL WORK ORDERS",
    ];
    return workOrderPermissions.some((p) => authData.permissions.includes(p));
  }, [authData.permissions]);

  return { user: authData.user, hasPermission, canAccessModule };
};
// --- END PERMISSION LOGIC ---

const PlusIcon = () => <IoAdd className="icon" aria-hidden="true" />;

function WorkOrder() {
  const { hasPermission, canAccessModule } = usePermissions();

  if (!canAccessModule()) {
    return (
      <div className="projects-page">
        <h1>Access Denied</h1>
        <p>You do not have permission to view the Work Orders module.</p>
      </div>
    );
  }

  return (
    <div className="projects-page">
      <header className="page-header-projects">
        <div className="header-text-content">
          <h1>Work Orders</h1>
          <p>View and manage all company work orders.</p>
        </div>
        {/* User can create a new Work Order if they have the 'CREATE WORK ORDER' permission */}
        {hasPermission("CREATE WORK ORDER") && (
          <NavLink to="/work-orders/new">
            <button className="create-project-button-header">
              <PlusIcon /> Create New Work Order
            </button>
          </NavLink>
        )}
      </header>
      <WorkOrderList />
    </div>
  );
}

export const WorkOrderFormPage = () => {
  let { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const workOrderId = id === "new" ? null : id;
  const isCreating = workOrderId === null;

  React.useEffect(() => {
    // Redirect if user tries to create without permission
    if (isCreating && !hasPermission("CREATE WORK ORDER")) {
      console.error(
        "Access Denied: You do not have permission to create a new work order."
      );
      navigate("/work-orders");
    }
    // Redirect if user tries to edit without permission.
    else if (!isCreating && !hasPermission("SET WORK ORDER")) {
      console.error(
        "Access Denied: You do not have permission to edit work orders."
      );
      navigate(`/work-orders`);
    }
  }, [id, isCreating, hasPermission, navigate]);

  // Render nothing while redirecting. This prevents a flicker of the form content.
  if (
    (isCreating && !hasPermission("CREATE WORK ORDER")) ||
    (!isCreating && !hasPermission("SET WORK ORDER"))
  ) {
    return null;
  }

  // If the checks pass, render the form page.
  return (
    <div className="projects-page">
      <header className="page-header-projects">
        <NavLink to="/work-orders">
          <button className="backbutton">Back To Work Orders</button>
        </NavLink>
      </header>
      <WorkOrderForm workOrderId={workOrderId} />
    </div>
  );
};

export default WorkOrder;
